import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { formatarAteDuasCasas, formatarReais } from '@/lib/formato';
import { explicarFalha } from '@/lib/pedido';
import { retornoDe, type RetornoDeclarado } from '@/lib/retorno';

import { api, type ApoioDoEstoque, type EnvioDeAplicacao, type EnvioDeItem, type EnvioDeMovimento } from './api';
import { quantoViraNoItem } from './conversao';
import { ROTULO_DA_CATEGORIA } from './saldo';

export { explicarFalha };

/**
 * O módulo 07 falando em domínio. Toda escrita invalida o estoque E a oficina:
 * a reserva e a baixa da peça aparecem nas duas telas.
 */
const CHAVE = ['estoque'] as const;

export const useApoioDoEstoque = () => useQuery({ queryKey: [...CHAVE, 'apoio'], queryFn: () => api.apoio() });
export const useSaldos = () =>
  useQuery({ queryKey: [...CHAVE, 'saldos'], queryFn: async () => (await api.saldos()).saldos });
export const useNecessidade = () =>
  useQuery({ queryKey: [...CHAVE, 'necessidade'], queryFn: async () => (await api.necessidade()).necessidade });
export const useReservas = () =>
  useQuery({ queryKey: [...CHAVE, 'reservas'], queryFn: async () => (await api.reservas()).reservas });

/**
 * Toda escrita do estoque declara o seu retorno (R04, p.32). O "em quê" —
 * o código e o nome do item e do depósito — vem do apoio que a tela já leu:
 * a resposta traz só o id.
 */
function useEscrita<A, R>(
  fazer: (argumento: A) => Promise<R>,
  montar: (resposta: R, argumento: A, apoio: ApoioDoEstoque | undefined) => RetornoDeclarado,
) {
  const cliente = useQueryClient();
  return useMutation({
    meta: { retorno: retornoDe<R, A>((r, a) => montar(r, a, cliente.getQueryData<ApoioDoEstoque>([...CHAVE, 'apoio']))) },
    mutationFn: fazer,
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: CHAVE });
      void cliente.invalidateQueries({ queryKey: ['oficina'] });
    },
  });
}

function item(apoio: ApoioDoEstoque | undefined, produtoId: string) {
  const p = apoio?.produtos.find((x) => x.id === produtoId);
  return p ? { alvo: { codigo: p.codigo, nome: p.nome }, unidade: p.unidade } : { alvo: undefined, unidade: '' };
}

function deposito(apoio: ApoioDoEstoque | undefined, depositoId: string | null | undefined): string | null {
  const d = apoio?.depositos.find((x) => x.id === depositoId);
  return d ? `${d.codigo} · ${d.nome}` : null;
}

const qtd = (n: number, unidade: string) => `${formatarAteDuasCasas(n)}${unidade ? ` ${unidade}` : ''}`;

const TITULO_DO_MOVIMENTO: Record<EnvioDeMovimento['tipo'], string> = {
  entrada: 'Entrada no estoque lançada',
  saida: 'Saída do estoque lançada',
  devolucao: 'Devolução ao estoque lançada',
  transferencia: 'Transferência entre depósitos lançada',
};

export const useMovimentar = () =>
  useEscrita(
    (envio: EnvioDeMovimento) => api.movimentar(envio),
    (_r, v, apoio) => {
      const { alvo, unidade } = item(apoio, v.produtoId);
      const destino = deposito(apoio, v.depositoDestinoId);
      /* 0088 · na embalagem, o retorno diz as duas: o digitado e o que entrou no saldo. */
      const conversao = v.conversaoId
        ? apoio?.produtos.find((x) => x.id === v.produtoId)?.conversoes?.find((c) => c.id === v.conversaoId)
        : undefined;
      return {
        titulo: TITULO_DO_MOVIMENTO[v.tipo],
        ...(alvo ? { alvo } : {}),
        fatos: [
          conversao
            ? `${qtd(v.quantidade, conversao.unidade)} = ${qtd(quantoViraNoItem(v.quantidade, conversao.fator), unidade)}`
            : qtd(v.quantidade, unidade),
          destino ? `de ${deposito(apoio, v.depositoId) ?? 'origem'} para ${destino}` : deposito(apoio, v.depositoId),
          v.custoUnitario
            ? `${formatarReais(v.custoUnitario)} por ${conversao?.unidade ?? (unidade || 'unidade')}`
            : v.documento
              ? `documento ${v.documento}`
              : null,
        ],
      };
    },
  );
export const useContar = () =>
  useEscrita(
    (envio: Parameters<typeof api.contar>[0]) => api.contar(envio),
    (r, v, apoio) => {
      const { alvo, unidade } = item(apoio, v.produtoId);
      const diferenca = r.diferenca ?? 0;
      return {
        titulo: 'Contagem registrada',
        tom: r.ajustado && diferenca !== 0 ? 'ressalva' : 'sucesso',
        ...(alvo ? { alvo } : {}),
        fatos: [`${qtd(v.contado, unidade)} contados`, deposito(apoio, v.depositoId)],
        efeito:
          r.ajustado && diferenca !== 0
            ? `O saldo foi ajustado em ${diferenca > 0 ? '+' : ''}${qtd(diferenca, unidade)}`
            : 'Bateu com o saldo do sistema',
      };
    },
  );
export const useCriarItem = () =>
  useEscrita(
    (envio: EnvioDeItem) => api.criarItem(envio),
    (r, v) => ({
      titulo: 'Item cadastrado',
      /* O codigo que o sistema criou (0131), e nao um digitado. */
      alvo: { codigo: r.codigo, nome: v.nome },
      fatos: [ROTULO_DA_CATEGORIA[v.categoria] ?? v.categoria, `em ${v.unidade}`, v.critico ? 'crítico' : null],
    }),
  );
export const useCriarDeposito = () =>
  useEscrita(
    (envio: { nome: string }) => api.criarDeposito(envio),
    (r, v) => ({ titulo: 'Depósito cadastrado', alvo: { codigo: r.codigo, nome: v.nome } }),
  );
export const usePosicionar = () =>
  useEscrita(
    (envio: Parameters<typeof api.posicionar>[0]) => api.posicionar(envio),
    (_r, v, apoio) => {
      const { alvo, unidade } = item(apoio, v.produtoId);
      return {
        titulo: 'Posição do item gravada',
        ...(alvo ? { alvo } : {}),
        fatos: [deposito(apoio, v.depositoId), v.endereco ? `endereço ${v.endereco}` : null, `mínimo de ${qtd(v.estoqueMinimo, unidade)}`],
      };
    },
  );

/* =================================================== 0088 · conversão e aplicação */

export const useConversoes = (produtoId: string | null) =>
  useQuery({
    queryKey: [...CHAVE, 'conversoes', produtoId],
    queryFn: async () => (await api.conversoes(produtoId ?? '')).conversoes,
    enabled: produtoId !== null,
  });

export const useCriarConversao = () =>
  useEscrita(
    (v: { produtoId: string; unidade: string; fator: number; doItem?: { codigo: string; nome: string; unidade: string } }) =>
      api.criarConversao(v.produtoId, { unidade: v.unidade, fator: v.fator }),
    (r, v, apoio) => {
      /* O item recém-cadastrado ainda não está no apoio: quem chama diz qual é. */
      const doApoio = item(apoio, v.produtoId);
      const alvo = doApoio.alvo ?? (v.doItem ? { codigo: v.doItem.codigo, nome: v.doItem.nome } : undefined);
      const unidade = doApoio.unidade || v.doItem?.unidade || '';
      return {
        titulo: 'Unidade de compra cadastrada',
        ...(alvo ? { alvo } : {}),
        fatos: [`${r.conversao.unidade} = ${qtd(v.fator, unidade)}`],
        efeito: `A compra e o movimento podem vir em ${r.conversao.unidade}; o saldo continua em ${unidade || 'unidade do item'}`,
      };
    },
  );

export const useDesativarConversao = () =>
  useEscrita(
    (v: { id: string; produtoId: string; unidade: string }) => api.desativarConversao(v.id),
    (_r, v, apoio) => {
      const { alvo } = item(apoio, v.produtoId);
      return {
        titulo: 'Unidade de compra desativada',
        ...(alvo ? { alvo } : {}),
        fatos: [v.unidade],
        efeito: 'Os movimentos antigos continuam com a conta da época',
      };
    },
  );

export const useAplicacoes = (produtoId: string | null) =>
  useQuery({
    queryKey: [...CHAVE, 'aplicacoes', produtoId],
    queryFn: () => api.aplicacoes(produtoId ?? ''),
    enabled: produtoId !== null,
  });

export const useValidarAplicacao = () =>
  useEscrita(
    (v: { produtoId: string; envio: EnvioDeAplicacao; onde: string }) => api.validarAplicacao(v.produtoId, v.envio),
    (_r, v, apoio) => {
      const { alvo } = item(apoio, v.produtoId);
      return {
        titulo: 'Aplicação validada',
        ...(alvo ? { alvo } : {}),
        fatos: [v.onde, v.envio.observacao ?? null],
        efeito: 'A lista técnica e a peça da O.S. deixam de avisar para esta máquina',
      };
    },
  );

export const useRetirarAplicacao = () =>
  useEscrita(
    (v: { id: string; produtoId: string; onde: string }) => api.retirarAplicacao(v.id),
    (_r, v, apoio) => {
      const { alvo } = item(apoio, v.produtoId);
      return {
        titulo: 'Aplicação retirada',
        ...(alvo ? { alvo } : {}),
        fatos: [v.onde],
        efeito: 'A lista técnica e a peça da O.S. voltam a avisar para esta máquina',
      };
    },
  );
